/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  CAUSES,
  OUTCOMES,
  CALC_PRESETS,
  CALC_DEFAULT,
  SPLIT_ROWS,
  IMPACT_COLORS,
  CP_FEATURE_DOTS,
  PAGES,
  PAGE_SECTIONS,
  LANGUAGE_LABELS,
  sectionId,
  fmt,
  mono,
} from "./data";
import plugLogo from "./images/plug-logo.png";
import plugMark from "./images/plug-mark.png";
import "./PlugLanding.css";

/**
 * The Plug marketing site — "What is Plug", "For donors", "For businesses" and
 * "For nonprofits" behind one nav, ported from `Plug Landing.dc.html`.
 *
 * The design switches the four pages with `state.page` rather than by URL, and
 * this does the same, so the whole site is one component per language. The
 * language variants (PlugEn / PlugZh / PlugCn) are thin wrappers that pass the
 * matching copy object; nothing here holds a user-facing string.
 */

/* Language switching pushes to these paths. routes-main.js serves them as
   plain Routes — not HomePageRoute, which reads the first two characters of a
   path as a language code and would bounce /PlugEn to /enugEn. This is the
   single place that has to agree with it. */
/* A visitor who never switches language stays on / and sees no path at all;
   switching gives the language its own. These are registered on Plug as plain
   Routes ahead of the HomePageRoute entries that would otherwise redirect a
   switch back to the browser's own language — see routes-main.js. */
const LANGUAGE_PATHS = { en: "/en", zh: "/zh", cn: "/cn" };

/* Where the marketing site hands over to the app (CP-318). The webapp is a
   single page that opens its own sign-in pane, so "Log in" and "Create a free
   account" are the same URL differing only in the pane it opens on, and
   "Browse nonprofits" is the page itself. The static export overrides all
   three with absolute URLs on the app's origin — see plug-impact/build.js. */
const WEBAPP_PATH = "/webapp";
const WEBAPP_LOGIN = WEBAPP_PATH + "?login=1";
const WEBAPP_SIGNUP = WEBAPP_PATH + "?login=signup";

/* The closing CTA's second button is "Browse nonprofits" on What is Plug and
   For donors, and goes to the marketplace. On the two organisation pages it is
   "Talk to our team" / "Check eligibility", which have nowhere to go yet. */
const CTA_SECONDARY_BROWSES = { [PAGES.ABOUT]: true, [PAGES.DONORS]: true };

const ICON_STROKE = {
  fill: "none",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const iconShop = (stroke, size) => (
  <svg viewBox="0 0 24 24" width={size} height={size} stroke={stroke} {...ICON_STROKE}>
    <path d="M3.2 9.6V20h17.6V9.6"></path>
    <path d="M2.4 9.6 4.6 4h14.8l2.2 5.6z"></path>
    <path d="M9.6 20v-5.4h4.8V20"></path>
  </svg>
);

const iconDonor = (stroke, size) => (
  <svg viewBox="0 0 24 24" width={size} height={size} stroke={stroke} {...ICON_STROKE}>
    <circle cx="8.8" cy="7.2" r="3.1"></circle>
    <path d="M3.2 19.6c0-3.1 2.5-5.6 5.6-5.6 1 0 1.9.2 2.7.7"></path>
    <path d="M18 12.9c-1-.9-2.6-.6-3.2.6-.4.9-.1 1.9.6 2.6l2.6 2.6 2.6-2.6c.7-.7 1-1.7.6-2.6-.6-1.2-2.2-1.5-3.2-.6z"></path>
  </svg>
);

const iconNonprofit = (stroke, size) => (
  <svg viewBox="0 0 24 24" width={size} height={size} stroke={stroke} {...ICON_STROKE}>
    <path d="M12 8.6c1.2-1.9 4.1-1.5 4.9.7.6 1.7-.4 3.2-1.5 4.2L12 16.9l-3.4-3.4C7.5 12.5 6.5 11 7.1 9.3 7.9 7.1 10.8 6.7 12 8.6z"></path>
    <path d="M4.2 15.6c0 3 2.6 5.4 5.7 5.4h4.2c3.1 0 5.7-2.4 5.7-5.4"></path>
  </svg>
);

/* The four "why list on Plug" cards each carry their own blue line icon. */
const NP_ICONS = {
  storefront: (
    <svg width="24" height="24" viewBox="0 0 24 24" stroke="#2b91d6" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 9.5h17V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V9.5Z"></path>
      <path d="M3 9.5 5 4h14l2 5.5"></path>
      <path d="M9.5 21v-5.5h5V21"></path>
    </svg>
  ),
  heart: (
    <svg width="24" height="24" viewBox="0 0 24 24" stroke="#2b91d6" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 8.5A4.2 4.2 0 0 0 12 6.9a4.2 4.2 0 0 0-8.5 1.6c0 3.6 4.6 6.7 8.5 9.5 3.9-2.8 8.5-5.9 8.5-9.5Z"></path>
      <path d="M4 20.5c2.6-1.3 5.3-2 8-2s5.4.7 8 2"></path>
    </svg>
  ),
  people: (
    <svg width="24" height="24" viewBox="0 0 24 24" stroke="#2b91d6" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="7.5" r="2.8"></circle>
      <circle cx="16.4" cy="7.5" r="2.8"></circle>
      <path d="M2.5 20v-1.4A4.1 4.1 0 0 1 6.6 14.5h2.8"></path>
      <path d="M21.5 20v-1.4a4.1 4.1 0 0 0-4.1-4.1h-2.8"></path>
      <path d="M9.6 20h4.8"></path>
    </svg>
  ),
  coin: (
    <svg width="24" height="24" viewBox="0 0 24 24" stroke="#2b91d6" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.6"></circle>
      <path d="M8.6 15.4c.6 1.1 1.9 1.8 3.4 1.8 2 0 3.4-1.1 3.4-2.7 0-1.6-1.3-2.3-3.4-2.7-2.1-.4-3.4-1.1-3.4-2.7 0-1.6 1.4-2.7 3.4-2.7 1.5 0 2.8.7 3.4 1.8"></path>
      <path d="M12 5.2v13.6"></path>
    </svg>
  ),
};

class PlugLanding extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: props.initialPage,
      amount: CALC_DEFAULT,
      open: 0,
    };
    this.goTo = this.goTo.bind(this);
    this.changeLanguage = this.changeLanguage.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  goTo(page) {
    return () => {
      this.setState({ page, open: 0 });
      window.scrollTo(0, 0);
    };
  }

  /**
   * Swap language by navigating to the sibling component. Falls back to a plain
   * i18n change when there is no router history — the page still renders, it
   * just cannot move to the other language's component.
   */
  changeLanguage(i18n) {
    return (event) => {
      const next = event.target.value;
      i18n.changeLanguage(next);
      const path = LANGUAGE_PATHS[next];
      if (path && this.props.history) {
        this.props.history.push(path);
      }
    };
  }

  /**
   * Anything that moves between the four pages.
   *
   * In the app a page is component state, so this is a click handler on
   * whatever element the design used. In the static export (plug-impact) each
   * page is its own file, so the same thing has to be a real anchor to be
   * followable and crawlable — `hrefForPage` switches it over.
   */
  pageLink(page, { className, children, as = "span", key }) {
    const { hrefForPage } = this.props;

    if (hrefForPage) {
      return (
        <a key={key} className={className} href={hrefForPage(page)}>
          {children}
        </a>
      );
    }

    const Tag = as;
    return (
      <Tag
        key={key}
        className={className}
        onClick={this.goTo(page)}
        {...(Tag === "button" ? { type: "button" } : {})}
      >
        {children}
      </Tag>
    );
  }

  /**
   * Anything that leaves the marketing site for the marketplace. Same split as
   * pageLink: a router link in the app, a plain anchor in the static export,
   * which has no router and points at the app's own origin instead.
   */
  /**
   * A link that leaves the marketing site for the app.
   *
   * Opens in a new tab, so the page the visitor was reading is still there
   * when they come back — the site and the app are separate things, and the
   * app has no way back to the site from most of its screens. rel is set
   * explicitly rather than relying on the implicit noopener modern browsers
   * apply, so the opened tab cannot reach window.opener on any of them.
   *
   * Only accountLink calls this. Navigation within the site goes through
   * pageLink, which stays in the tab.
   */
  appLink(href, { className, children }) {
    const away = { target: "_blank", rel: "noopener noreferrer" };

    if (this.props.hrefForPage) {
      return (
        <a className={className} href={href} {...away}>
          {children}
        </a>
      );
    }

    return (
      <Link className={className} to={href} {...away}>
        {children}
      </Link>
    );
  }

  /**
   * A webapp hand-off, or nothing at all when `showAccountCtas` is off.
   *
   * Every button that leaves for the app goes through this rather than calling
   * appLink directly, so the switch cannot be true of some of them and false
   * of others — which is what it promises, and what it did not do while only
   * the What is Plug hero and the closing CTA honoured it.
   */
  accountLink(href, options) {
    if (!this.props.showAccountCtas) return null;
    return this.appLink(href, options);
  }

  /**
   * The hero and closing-CTA sign-up button — "Create a free account" on What
   * is Plug, and the audience pages' own wordings for the same thing. Narrower
   * than showAccountCtas, which also takes the nav's "Sign up" and every
   * "Browse nonprofits" with it.
   */
  signupCtaLink(options) {
    if (!this.props.showSignupCta) return null;
    return this.accountLink(this.props.signupHref, options);
  }

  /** "Browse nonprofits", wherever it appears. Same shape as signupCtaLink. */
  browseLink(options) {
    if (!this.props.showBrowse) return null;
    return this.accountLink(this.props.browseHref, options);
  }

  toggleFaq(index) {
    return () => this.setState((prev) => ({ open: prev.open === index ? -1 : index }));
  }

  pickAmount(value) {
    return () => this.setState({ amount: value });
  }

  /* Which FAQ list belongs to the page on screen. "What is Plug" shares the
     donor list, as it does in the design. */
  currentFaqs() {
    const { copy } = this.props;
    const { page } = this.state;
    if (page === PAGES.BUSINESS) return copy.faqs.business;
    if (page === PAGES.NONPROFIT) return copy.faqs.nonprofit;
    return copy.faqs.donors;
  }

  /* Annual outcome counts for the giving calculator. A share that does not buy
     a whole unit is shown to one decimal rather than rounded away to zero. */
  outcomeRows() {
    const { copy } = this.props;
    const year = this.state.amount * 12;

    return OUTCOMES.map((outcome) => {
      const text = copy.outcomes[outcome.id];
      const raw = (year * outcome.share) / outcome.unit;
      const partial = raw < 1;
      const whole = Math.round(raw);

      return {
        id: outcome.id,
        name: text.name,
        mono: outcome.mono,
        color: outcome.color,
        tint: outcome.tint,
        pct: Math.round(outcome.share * 100) + "%",
        amountLabel: fmt(year * outcome.share),
        count: partial ? raw.toFixed(1) : whole.toLocaleString("en-US"),
        noun: partial || whole === 1 ? text.one : text.many,
      };
    });
  }

  renderNav(i18n) {
    const { copy, lang } = this.props;
    const { page } = this.state;
    const tabs = [
      { id: PAGES.ABOUT, label: copy.nav.about },
      { id: PAGES.DONORS, label: copy.nav.donors },
      { id: PAGES.BUSINESS, label: copy.nav.business },
      { id: PAGES.NONPROFIT, label: copy.nav.nonprofit },
    ];

    return (
      <div className="PlugLanding-nav">
        {this.pageLink(PAGES.ABOUT, {
          as: "div",
          className: "PlugLanding-navLogo",
          children: (
            <React.Fragment>
              <img src={plugLogo} alt={copy.nav.logoAlt} />
              <span className="PlugLanding-navBeta">{copy.nav.beta}</span>
            </React.Fragment>
          ),
        })}

        {/* With the audience pages hidden there is only one page to be on, so
            the tab strip goes entirely rather than leaving a lone tab. */}
        {!!this.props.showAudiencePages && (
          <div className="PlugLanding-navTabs">
            {tabs.map((tab) =>
              this.pageLink(tab.id, {
                key: tab.id,
                as: "button",
                className: "PlugLanding-navTab" + (page === tab.id ? " is-active" : ""),
                children: tab.label,
              }),
            )}
          </div>
        )}

        <div className="PlugLanding-navRight">
          {!!this.props.showLogin &&
            this.accountLink(this.props.loginHref, {
              className: "PlugLanding-navLogin",
              children: copy.nav.login,
            })}

          {!!this.props.showNavCta &&
            this.accountLink(this.props.signupHref, {
              className: "PlugLanding-navCta",
              children: copy.nav.cta[page],
            })}

          <div className="PlugLanding-lang">
            <div className="PlugLanding-langGlobe" />
            {/* The selector shows the language of the page you are on, not
                i18n's — PlugZh is Traditional Chinese whatever i18n last
                detected, so reading i18n.language here would label it English. */}
            <select
              className="PlugLanding-langSelect"
              onChange={this.changeLanguage(i18n)}
              value={lang}
            >
              {Object.keys(LANGUAGE_LABELS).map((code) => (
                <option key={code} value={code}>
                  {LANGUAGE_LABELS[code]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  renderAbout() {
    const { copy } = this.props;
    const about = copy.about;

    return (
      <div className="PlugLanding-page">
        <div className="PlugLanding-aboutHero">
          <div className="PlugLanding-pill">
            <span className="PlugLanding-pillDot" />
            <span className="PlugLanding-pillTxt">{about.pill}</span>
          </div>
          <h1 className="PlugLanding-aboutH1">{about.h1}</h1>
          <p className="PlugLanding-aboutLede">{about.lede}</p>
          <div className="PlugLanding-btnRow PlugLanding-btnRowCenter">
            {/* The homepage's way into the app. Browse rather than sign-up:
                the nav's own pill is the sign-up route, and someone who has
                not read the page yet is likelier to want a look than an
                account. Optional, the way the closing CTA's buttons are. */}
            {!!about.ctaPrimary &&
              this.browseLink({
                className: "PlugLanding-btnPrimary",
                children: about.ctaPrimary,
              })}
            <a href={"#" + sectionId("flow")} className="PlugLanding-btnGhost">
              {about.ctaSecondary}
            </a>
          </div>
        </div>

        <div className="PlugLanding-flowWrap" id={sectionId("flow")}>
          <div className="PlugLanding-flow">
            <div className="PlugLanding-flowHead">
              <div className="PlugLanding-flowKicker">{about.flow.kicker}</div>
              <h2 className="PlugLanding-flowTitle">{about.flow.title}</h2>
            </div>

            <div className="PlugLanding-flowRow">
              <div className="PlugLanding-flowCard">
                <div className="PlugLanding-flowCardHead">
                  <span className="PlugLanding-flowIcon PlugLanding-flowIconGreen">
                    {iconShop("#2f8f6c", 19)}
                  </span>
                  <span className="PlugLanding-flowCardName">{about.flow.business.name}</span>
                </div>
                <p className="PlugLanding-flowCardTxt">{about.flow.business.txt}</p>
              </div>

              <div className="PlugLanding-flowArrows">
                <div>
                  <div className="PlugLanding-flowLine PlugLanding-flowLineOut">
                    <span className="PlugLanding-flowTip PlugLanding-flowTipRight PlugLanding-flowTipBlue">▶</span>
                  </div>
                  <div className="PlugLanding-flowLabel PlugLanding-flowLabelBlue">{about.flow.bizOut}</div>
                </div>
                <div>
                  <div className="PlugLanding-flowLine PlugLanding-flowLineBack">
                    <span className="PlugLanding-flowTip PlugLanding-flowTipLeft PlugLanding-flowTipGreen">◀</span>
                  </div>
                  <div className="PlugLanding-flowLabel PlugLanding-flowLabelGreen PlugLanding-flowLabelRight">
                    {about.flow.bizBack}
                  </div>
                </div>
              </div>

              <div className="PlugLanding-flowHub">
                <img src={plugLogo} alt={copy.nav.logoAlt} />
                <p className="PlugLanding-flowHubTxt">{about.flow.hub}</p>
              </div>

              <div className="PlugLanding-flowArrows">
                <div>
                  <div className="PlugLanding-flowLine PlugLanding-flowLineOutRev">
                    <span className="PlugLanding-flowTip PlugLanding-flowTipLeft PlugLanding-flowTipBlue">◀</span>
                  </div>
                  <div className="PlugLanding-flowLabel PlugLanding-flowLabelBlue PlugLanding-flowLabelRight">
                    {about.flow.donorOut}
                  </div>
                </div>
                <div>
                  <div className="PlugLanding-flowLine PlugLanding-flowLineBackRev">
                    <span className="PlugLanding-flowTip PlugLanding-flowTipRight PlugLanding-flowTipGreen">▶</span>
                  </div>
                  <div className="PlugLanding-flowLabel PlugLanding-flowLabelGreen">{about.flow.donorBack}</div>
                </div>
              </div>

              <div className="PlugLanding-flowCard">
                <div className="PlugLanding-flowCardHead">
                  <span className="PlugLanding-flowIcon PlugLanding-flowIconBlue">
                    {iconDonor("#175b89", 19)}
                  </span>
                  <span className="PlugLanding-flowCardName">{about.flow.donors.name}</span>
                </div>
                <p className="PlugLanding-flowCardTxt">{about.flow.donors.txt}</p>
              </div>
            </div>

            <div className="PlugLanding-flowDown">
              <div className="PlugLanding-flowDownCol">
                <div className="PlugLanding-flowStemBlue" />
                <div className="PlugLanding-flowCaret PlugLanding-flowCaretDown">▼</div>
                <div className="PlugLanding-flowDownTxt PlugLanding-flowLabelBlue">{about.flow.npDown}</div>
              </div>
              <div className="PlugLanding-flowDownCol">
                <div className="PlugLanding-flowCaret PlugLanding-flowCaretUp">▲</div>
                <div className="PlugLanding-flowStemGreen" />
                <div className="PlugLanding-flowDownTxt PlugLanding-flowLabelGreen">{about.flow.npUp}</div>
              </div>
            </div>

            <div className="PlugLanding-flowNpRow">
              <div className="PlugLanding-flowNpCard">
                <div className="PlugLanding-flowCardHead">
                  <span className="PlugLanding-flowIcon PlugLanding-flowIconSlate">
                    {iconNonprofit("#2b6f9c", 19)}
                  </span>
                  <span className="PlugLanding-flowCardName">{about.flow.nonprofits.name}</span>
                </div>
                <p className="PlugLanding-flowCardTxt">{about.flow.nonprofits.txt}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="PlugLanding-sides" id={sectionId("sides")}>
          <div className="PlugLanding-sideGrid">
            {about.sides.map((side) => (
              <div className="PlugLanding-sideCard" key={side.id}>
                <div className="PlugLanding-sideHead">
                  <span
                    className="PlugLanding-sideIcon"
                    style={{
                      background:
                        side.id === "business" ? "#e8f7f1" : side.id === "donors" ? "#e6f5ff" : "#eef4f8",
                    }}
                  >
                    {side.id === "business"
                      ? iconShop("#2f8f6c", 19)
                      : side.id === "donors"
                        ? iconDonor("#175b89", 19)
                        : iconNonprofit("#3c3f43", 19)}
                  </span>
                  <div
                    className="PlugLanding-sideKicker"
                    style={{
                      color: side.id === "business" ? "#2f8f6c" : side.id === "donors" ? "#175b89" : "#3c3f43",
                    }}
                  >
                    {side.kicker}
                  </div>
                </div>
                <h3 className="PlugLanding-sideTitle">{side.title}</h3>
                <p className="PlugLanding-sideTxt">{side.txt}</p>
                {!!this.props.showAudiencePages &&
                  this.pageLink(
                    side.id === "business"
                      ? PAGES.BUSINESS
                      : side.id === "donors"
                        ? PAGES.DONORS
                        : PAGES.NONPROFIT,
                    { className: "PlugLanding-sideLink", children: side.link },
                  )}
              </div>
            ))}
          </div>
        </div>

        <div className="PlugLanding-values" id={sectionId("values")}>
          <div className="PlugLanding-valuesInner">
            <h2 className="PlugLanding-valuesTitle">{about.valuesTitle}</h2>
            <div className="PlugLanding-valueGrid">
              {about.values.map((value) => (
                <div key={value.title}>
                  <div className="PlugLanding-valueRule" />
                  <div className="PlugLanding-valueTitle">{value.title}</div>
                  <p className="PlugLanding-valueTxt">{value.txt}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderDonors() {
    const { copy, showPressStrip, showCalculator } = this.props;
    const donors = copy.donors;
    const rows = this.outcomeRows();

    return (
      <div className="PlugLanding-page">
        <div className="PlugLanding-hero">
          <div>
            <div className="PlugLanding-pill">
              <span className="PlugLanding-pillDot" />
              <span className="PlugLanding-pillTxt">{donors.pill}</span>
            </div>
            <h1 className="PlugLanding-h1">{donors.h1}</h1>
            <p className="PlugLanding-lede" style={{ maxWidth: 520 }}>
              {donors.lede}
            </p>
            {!!this.props.showAccountCtas && (
              <div className="PlugLanding-btnRow">
                {this.signupCtaLink({
                  className: "PlugLanding-btnPrimary",
                  children: donors.ctaPrimary,
                })}
                {this.browseLink({
                  className: "PlugLanding-btnGhost",
                  children: donors.ctaSecondary,
                })}
              </div>
            )}
            <div className="PlugLanding-note" style={{ maxWidth: 480 }}>
              {donors.note}
            </div>
          </div>
          <div>
            <div className="PlugLanding-shot PlugLanding-shotBlue">{donors.shot}</div>
          </div>
        </div>

        {!!showPressStrip && (
          <div className="PlugLanding-pressWrap">
            <div className="PlugLanding-press">
              <span className="PlugLanding-pressLabel">{donors.press.label}</span>
              <div className="PlugLanding-pressLogos">
                {[0, 1, 2, 3, 4].map((slot) => (
                  <div className="PlugLanding-pressLogo" key={slot}>
                    {donors.press.logo}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="PlugLanding-band" id={sectionId("how")}>
          <div className="PlugLanding-intro">
            <div className="PlugLanding-eyebrow">{donors.how.kicker}</div>
            <h2 className="PlugLanding-h2">{donors.how.h2}</h2>
            <p className="PlugLanding-body">{donors.how.lede}</p>
          </div>
          <div className="PlugLanding-grid3">
            {donors.how.steps.map((step, index) => (
              <div key={step.title}>
                <div className="PlugLanding-stepHead">
                  <span className="PlugLanding-stepNum">{index + 1}</span>
                  <span className="PlugLanding-stepRule" />
                </div>
                <h3 className="PlugLanding-h3">{step.title}</h3>
                <p className="PlugLanding-stepTxt">{step.txt}</p>
                <div className="PlugLanding-stepShot">{step.shot}</div>
              </div>
            ))}
          </div>
        </div>

        {!!showCalculator && (
          <div className="PlugLanding-section" id={sectionId("calculator")}>
            <div className="PlugLanding-calc">
              <div>
                <div className="PlugLanding-eyebrow">{donors.calc.kicker}</div>
                <h2 className="PlugLanding-h2Small">{donors.calc.h2}</h2>
                <p className="PlugLanding-calcLede">{donors.calc.lede}</p>
                <div className="PlugLanding-calcBox">
                  <div className="PlugLanding-calcLabel">{donors.calc.label}</div>
                  <div className="PlugLanding-calcAmount">
                    <span className="PlugLanding-calcCcy">{donors.calc.currency}</span>
                    <span className="PlugLanding-calcValue">
                      {this.state.amount.toLocaleString("en-US")}
                    </span>
                    <span className="PlugLanding-calcPer">{donors.calc.per}</span>
                  </div>
                  <div className="PlugLanding-calcPresets">
                    {CALC_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        data-plug-preset={preset}
                        className={
                          "PlugLanding-calcPreset" + (this.state.amount === preset ? " is-active" : "")
                        }
                        onClick={this.pickAmount(preset)}
                      >
                        {fmt(preset)}
                      </button>
                    ))}
                  </div>
                  <div className="PlugLanding-calcFoot">
                    {donors.calc.footBefore}
                    <b>{fmt(this.state.amount * 12)}</b>
                    {donors.calc.footAfter}
                  </div>
                </div>
              </div>

              <div className="PlugLanding-calcRows">
                {rows.map((row) => (
                  <div className="PlugLanding-outcome" key={row.id} data-plug-outcome={row.id}>
                    <div className="PlugLanding-outcomeHead">
                      <div
                        className="PlugLanding-outcomeMono"
                        style={{ background: row.tint, color: row.color }}
                      >
                        {row.mono}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="PlugLanding-outcomeName">{row.name}</div>
                        <div className="PlugLanding-outcomeMeta">
                          {row.pct} · <span data-plug-share-amount>{row.amountLabel}</span>{" "}
                          {donors.calc.yearly}
                        </div>
                      </div>
                    </div>
                    <div className="PlugLanding-outcomePhoto" style={{ background: row.tint }}>
                      {donors.calc.photo}
                    </div>
                    <div className="PlugLanding-outcomeBody">
                      <div className="PlugLanding-outcomeCount" style={{ color: row.color }}>
                        {row.count}
                      </div>
                      <div className="PlugLanding-outcomeNoun">{row.noun}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="PlugLanding-section PlugLanding-sectionTop">
          <div className="PlugLanding-intro">
            <div className="PlugLanding-eyebrow">{donors.onePayment.kicker}</div>
            <h2 className="PlugLanding-h2">{donors.onePayment.h2}</h2>
            <p className="PlugLanding-body">{donors.onePayment.lede}</p>
          </div>

          <div className="PlugLanding-grid2">
            <div className="PlugLanding-panel">
              <h3 className="PlugLanding-panelTitle">{donors.onePayment.give.title}</h3>
              <p className="PlugLanding-panelTxt">{donors.onePayment.give.txt}</p>
              <div className="PlugLanding-splitBox">
                <div className="PlugLanding-splitHead">
                  <span className="PlugLanding-splitHeadLabel">{donors.onePayment.give.oneLabel}</span>
                  <span className="PlugLanding-splitHeadValue">{donors.onePayment.give.oneValue}</span>
                </div>
                <div className="PlugLanding-splitBar">
                  {SPLIT_ROWS.map((row) => (
                    <div key={row.id} style={{ width: row.pct + "%", background: row.color }} />
                  ))}
                </div>
                <div className="PlugLanding-splitRows">
                  {SPLIT_ROWS.map((row) => (
                    <div className="PlugLanding-splitRow" key={row.id}>
                      <span className="PlugLanding-splitDot" style={{ background: row.color }} />
                      <span className="PlugLanding-splitName">{copy.outcomes[row.id].name}</span>
                      <span className="PlugLanding-splitPct">{row.pct}%</span>
                      <span className="PlugLanding-splitAmt">{fmt(row.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="PlugLanding-panelFoot">
                {donors.onePayment.give.checks.map((check) => (
                  <div className="PlugLanding-check" key={check}>
                    <span className="PlugLanding-checkMark">✓</span>
                    <span className="PlugLanding-checkTxt">{check}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="PlugLanding-panel">
              <h3 className="PlugLanding-panelTitle">{donors.onePayment.receipts.title}</h3>
              <p className="PlugLanding-panelTxt">{donors.onePayment.receipts.txt}</p>
              <div className="PlugLanding-receipts">
                <div className="PlugLanding-receiptsHead">
                  <span className="PlugLanding-receiptsLabel">{donors.onePayment.receipts.label}</span>
                  <span className="PlugLanding-receiptsNote">{donors.onePayment.receipts.note}</span>
                </div>
                {donors.onePayment.receipts.rows.map((row) => (
                  <div className="PlugLanding-receiptRow" key={row.detail}>
                    <div style={{ flex: 1 }}>
                      <div className="PlugLanding-receiptDate">{row.date}</div>
                      <div className="PlugLanding-receiptDetail">{row.detail}</div>
                    </div>
                    <span className="PlugLanding-receiptAmt">{row.amount}</span>
                    <span className="PlugLanding-receiptPdf">{donors.onePayment.receipts.pdf}</span>
                  </div>
                ))}
                <div className="PlugLanding-receiptTotal">
                  <span className="PlugLanding-receiptTotalLabel">
                    {donors.onePayment.receipts.totalLabel}
                  </span>
                  <span className="PlugLanding-receiptTotalValue">
                    {donors.onePayment.receipts.totalValue}
                  </span>
                </div>
              </div>
              <div className="PlugLanding-panelFoot">
                {donors.onePayment.receipts.checks.map((check) => (
                  <div className="PlugLanding-check" key={check}>
                    <span className="PlugLanding-checkMark PlugLanding-checkMarkGreen">✓</span>
                    <span className="PlugLanding-checkTxt">{check}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="PlugLanding-band" id={sectionId("causes")}>
          <div className="PlugLanding-introRow">
            <div className="PlugLanding-intro" style={{ maxWidth: 600 }}>
              <div className="PlugLanding-eyebrow">{donors.marketplace.kicker}</div>
              <h2 className="PlugLanding-h2">{donors.marketplace.h2}</h2>
              <p className="PlugLanding-body">{donors.marketplace.lede}</p>
            </div>
            {this.browseLink({
              className: "PlugLanding-btnOutline",
              children: donors.marketplace.browse,
            })}
          </div>
          <div className="PlugLanding-causeGrid">
            {CAUSES.map((cause) => (
              <div className="PlugLanding-cause" key={cause.id}>
                <div className="PlugLanding-causeIcon" style={{ background: cause.tint }}>
                  <span className="PlugLanding-causeDot" style={{ background: cause.color }} />
                </div>
                <div className="PlugLanding-causeLabel">{copy.causes[cause.id].label}</div>
                <div className="PlugLanding-causeBlurb">{copy.causes[cause.id].blurb}</div>
                <div className="PlugLanding-causeCount">
                  {cause.count}
                  {donors.marketplace.countSuffix}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="PlugLanding-impact" id={sectionId("impact")}>
          <div className="PlugLanding-intro">
            <div className="PlugLanding-eyebrow">{donors.impact.kicker}</div>
            <h2 className="PlugLanding-h2">{donors.impact.h2}</h2>
            <p className="PlugLanding-body">{donors.impact.lede}</p>
          </div>
          <div className="PlugLanding-impactGrid">
            {donors.impact.stats.map((stat, index) => (
              <div className="PlugLanding-impactCard" key={stat.txt}>
                <div className="PlugLanding-impactNum" style={{ color: IMPACT_COLORS[index] }}>
                  {stat.num}
                </div>
                <div className="PlugLanding-impactTxt">{stat.txt}</div>
              </div>
            ))}
          </div>
          <div className="PlugLanding-quote">
            <div>
              <div className="PlugLanding-quoteTxt">{donors.impact.quote.txt}</div>
              <div className="PlugLanding-quoteWho">
                <div className="PlugLanding-quoteMono">{donors.impact.quote.mono}</div>
                <div>
                  <div className="PlugLanding-quoteName">{donors.impact.quote.name}</div>
                  <div className="PlugLanding-quoteMeta">{donors.impact.quote.meta}</div>
                </div>
              </div>
            </div>
            <div className="PlugLanding-photo">{donors.impact.quote.photo}</div>
          </div>
        </div>

        <div className="PlugLanding-trust">
          <div className="PlugLanding-trustGrid">
            {donors.trust.map((item) => (
              <div key={item.title}>
                <div className="PlugLanding-trustKicker">{item.kicker}</div>
                <h3 className="PlugLanding-trustTitle">{item.title}</h3>
                <p className="PlugLanding-trustTxt">{item.txt}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Nothing but routes to the two hidden pages. */}
        {!!this.props.showAudiencePages && (
          <div className="PlugLanding-cross">
            <div className="PlugLanding-grid2">
              {donors.cross.map((item) =>
                this.pageLink(item.id === "business" ? PAGES.BUSINESS : PAGES.NONPROFIT, {
                  key: item.id,
                  as: "div",
                  className: "PlugLanding-crossCard",
                  children: (
                    <React.Fragment>
                      <div
                        className="PlugLanding-crossKicker"
                        style={{ color: item.id === "business" ? "#45b58a" : "#175b89" }}
                      >
                        {item.kicker}
                      </div>
                      <h3 className="PlugLanding-crossTitle">{item.title}</h3>
                      <p className="PlugLanding-crossTxt">{item.txt}</p>
                      <span className="PlugLanding-crossLink">{item.link}</span>
                    </React.Fragment>
                  ),
                }),
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderBusiness() {
    const { copy } = this.props;
    const business = copy.business;

    return (
      <div className="PlugLanding-page">
        <div className="PlugLanding-hero" style={{ paddingTop: 72 }}>
          <div>
            <div className="PlugLanding-pill PlugLanding-pillGreen">
              <span className="PlugLanding-pillDot" />
              <span className="PlugLanding-pillTxt">{business.pill}</span>
            </div>
            <h1 className="PlugLanding-h1 PlugLanding-h1Small">{business.h1}</h1>
            <p className="PlugLanding-lede">{business.lede}</p>
            <div className="PlugLanding-btnRow">
              {this.signupCtaLink({
                className: "PlugLanding-btnPrimary",
                children: business.ctaPrimary,
              })}
              <a href="#" className="PlugLanding-btnGhost">
                {business.ctaSecondary}
              </a>
            </div>
            <div className="PlugLanding-note">{business.note}</div>
          </div>
          <div>
            <div className="PlugLanding-shot PlugLanding-shotGreen">{business.shot}</div>
          </div>
        </div>

        <div className="PlugLanding-band" id={sectionId("rules")}>
          <div className="PlugLanding-intro PlugLanding-introNarrow">
            <div className="PlugLanding-eyebrow">{business.rules.kicker}</div>
            <h2 className="PlugLanding-h2">{business.rules.h2}</h2>
            <p className="PlugLanding-body">{business.rules.lede}</p>
          </div>
          <div className="PlugLanding-ruleGrid">
            {business.rules.cards.map((card) => (
              <div className="PlugLanding-card" key={card.title}>
                <div className="PlugLanding-ruleTag">{card.tag}</div>
                <div className="PlugLanding-ruleHeadline">{card.headline}</div>
                <h3 className="PlugLanding-ruleTitle">{card.title}</h3>
                <p className="PlugLanding-ruleTxt">{card.txt}</p>
                <div className="PlugLanding-ruleNote">{card.note}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="PlugLanding-section" id={sectionId("customers")}>
          <div className="PlugLanding-split">
            <div>
              <div className="PlugLanding-eyebrow">{business.customers.kicker}</div>
              <h2 className="PlugLanding-h2Small">{business.customers.h2}</h2>
              <p className="PlugLanding-body" style={{ marginBottom: 26 }}>
                {business.customers.lede}
              </p>
              <div className="PlugLanding-checkList">
                {business.customers.checks.map((check) => (
                  <div className="PlugLanding-check" key={check}>
                    <span className="PlugLanding-checkMark PlugLanding-checkMarkGreen">✓</span>
                    <span className="PlugLanding-checkTxt">{check}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="PlugLanding-proofCol">
              <div className="PlugLanding-badge">
                <img src={plugMark} alt={copy.nav.logoAlt} />
                <div>
                  <div className="PlugLanding-badgeTitle">{business.customers.badgeTitle}</div>
                  <div className="PlugLanding-badgeTxt">{business.customers.badgeTxt}</div>
                </div>
              </div>

              <div className="PlugLanding-mockReceipt">
                <div className="PlugLanding-mockLabel">{business.customers.receiptLabel}</div>
                <div className="PlugLanding-mockLine">
                  <span>{business.customers.subtotalLabel}</span>
                  <span>{business.customers.subtotalValue}</span>
                </div>
                <div className="PlugLanding-mockRule" />
                <div className="PlugLanding-mockTotal">
                  <span>{business.customers.totalLabel}</span>
                  <span>{business.customers.totalValue}</span>
                </div>
                <div className="PlugLanding-mockFoot">
                  {business.customers.receiptFootBefore}
                  <b>{business.customers.receiptFootWe}</b>
                  {business.customers.receiptFootMid}
                  <b>{business.customers.receiptFootAmount}</b>
                  {business.customers.receiptFootAfter}
                </div>
              </div>

              <div className="PlugLanding-mockPhoto">{business.customers.photo}</div>
            </div>
          </div>
        </div>

        <div className="PlugLanding-band" id={sectionId("directory")}>
          <div className="PlugLanding-introRow" style={{ marginBottom: 32 }}>
            <div className="PlugLanding-intro" style={{ maxWidth: 560 }}>
              <div className="PlugLanding-eyebrow">{business.directory.kicker}</div>
              <h2 className="PlugLanding-h2Small" style={{ margin: 0 }}>
                {business.directory.h2}
              </h2>
            </div>
            <a href="#" className="PlugLanding-linkRight">
              {business.directory.link}
            </a>
          </div>
          <div className="PlugLanding-bizGrid">
            {business.directory.rows.map((row) => (
              <div className="PlugLanding-bizRow" key={row.name}>
                <div className="PlugLanding-bizMono">{mono(row.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="PlugLanding-bizName">{row.name}</div>
                  <div className="PlugLanding-bizMeta">
                    {row.industry} · {row.district}
                  </div>
                </div>
                <div className="PlugLanding-bizRule">{row.rule}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="PlugLanding-section">
          <div className="PlugLanding-quote" style={{ padding: "36px 40px" }}>
            <div>
              <div className="PlugLanding-quoteTxt">{business.quote.txt}</div>
              <div className="PlugLanding-quoteWho">
                <div className="PlugLanding-quoteMono PlugLanding-quoteMonoGrey">{business.quote.mono}</div>
                <div>
                  <div className="PlugLanding-quoteName">{business.quote.name}</div>
                  <div className="PlugLanding-quoteMeta">{business.quote.meta}</div>
                </div>
              </div>
            </div>
            <div className="PlugLanding-photo">{business.quote.photo}</div>
          </div>
        </div>
      </div>
    );
  }

  renderNonprofit() {
    const { copy } = this.props;
    const nonprofit = copy.nonprofit;

    return (
      <div className="PlugLanding-page">
        <div className="PlugLanding-hero" style={{ paddingTop: 72 }}>
          <div>
            <div className="PlugLanding-pill PlugLanding-pillSlate">
              <span className="PlugLanding-pillDot" />
              <span className="PlugLanding-pillTxt">{nonprofit.pill}</span>
            </div>
            <h1 className="PlugLanding-h1 PlugLanding-h1Small">{nonprofit.h1}</h1>
            <p className="PlugLanding-lede">
              {nonprofit.ledeParts[0]}
              <br />
              <br />
              {nonprofit.ledeParts[1]}
            </p>
            <div className="PlugLanding-btnRow">
              {this.signupCtaLink({
                className: "PlugLanding-btnPrimary",
                children: nonprofit.ctaPrimary,
              })}
              <a href="#" className="PlugLanding-btnGhost">
                {nonprofit.ctaSecondary}
              </a>
            </div>
            <div className="PlugLanding-note">{nonprofit.note}</div>
          </div>
          <div>
            <div className="PlugLanding-shot PlugLanding-shotSlate">{nonprofit.shot}</div>
          </div>
        </div>

        <div className="PlugLanding-band" id={sectionId("why")}>
          <div className="PlugLanding-intro PlugLanding-introNarrow">
            <div className="PlugLanding-eyebrow">{nonprofit.why.kicker}</div>
            <h2 className="PlugLanding-h2">{nonprofit.why.h2}</h2>
          </div>
          <div className="PlugLanding-grid4">
            {nonprofit.why.cards.map((card) => (
              <div className="PlugLanding-npCard" key={card.id}>
                <div className="PlugLanding-npIcon">{NP_ICONS[card.id]}</div>
                <h3 className="PlugLanding-npTitle">{card.title}</h3>
                <p className="PlugLanding-npTxt">{card.txt}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="PlugLanding-section" id={sectionId("dashboard")}>
          <div className="PlugLanding-split">
            <div>
              <div className="PlugLanding-eyebrow">{nonprofit.dashboard.kicker}</div>
              <h2 className="PlugLanding-h2Small">{nonprofit.dashboard.h2}</h2>
              <p className="PlugLanding-body" style={{ marginBottom: 26 }}>
                {nonprofit.dashboard.lede}
              </p>
              <div className="PlugLanding-checkList">
                {nonprofit.dashboard.checks.map((check) => (
                  <div className="PlugLanding-check" key={check}>
                    <span className="PlugLanding-checkMark">✓</span>
                    <span className="PlugLanding-checkTxt">{check}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="PlugLanding-npDash">
              <div className="PlugLanding-npDashHead">
                <div className="PlugLanding-npDashLabel">{nonprofit.dashboard.panelLabel}</div>
                <div className="PlugLanding-npDashValue">{nonprofit.dashboard.panelValue}</div>
              </div>
              <div className="PlugLanding-npDashBar">
                <div style={{ width: "62%", background: "#2b91d6" }} />
                <div style={{ width: "38%", background: "#45b58a" }} />
              </div>
              <div className="PlugLanding-npDashBody">
                <div className="PlugLanding-npDashRow">
                  <span className="PlugLanding-npDashDot" style={{ background: "#2b91d6" }} />
                  <div style={{ flex: 1 }}>
                    <div className="PlugLanding-npDashName">{nonprofit.dashboard.individuals.name}</div>
                    <div className="PlugLanding-npDashMeta">{nonprofit.dashboard.individuals.meta}</div>
                  </div>
                  <span className="PlugLanding-npDashPct">{nonprofit.dashboard.individuals.pct}</span>
                </div>
                <div className="PlugLanding-npDashRow">
                  <span className="PlugLanding-npDashDot" style={{ background: "#45b58a" }} />
                  <div style={{ flex: 1 }}>
                    <div className="PlugLanding-npDashName">{nonprofit.dashboard.businesses.name}</div>
                    <div className="PlugLanding-npDashMeta">{nonprofit.dashboard.businesses.meta}</div>
                  </div>
                  <span className="PlugLanding-npDashPct">{nonprofit.dashboard.businesses.pct}</span>
                </div>
                <div className="PlugLanding-npDashRule" />
                <div className="PlugLanding-npDashStats">
                  {nonprofit.dashboard.stats.map((stat, index) => (
                    <div key={stat.txt}>
                      <div
                        className="PlugLanding-npDashStatNum"
                        style={{ color: index === 0 ? "#45b58a" : "#606266" }}
                      >
                        {stat.num}
                      </div>
                      <div className="PlugLanding-npDashStatTxt">{stat.txt}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="PlugLanding-section PlugLanding-sectionTop" id={sectionId("checkplus")}>
          <div className="PlugLanding-introRow" style={{ marginBottom: 40 }}>
            <div className="PlugLanding-intro">
              <div className="PlugLanding-cpPill">
                <span className="PlugLanding-cpPillDot" />
                <span className="PlugLanding-cpPillTxt">{nonprofit.checkplus.pill}</span>
              </div>
              <h2 className="PlugLanding-h2">{nonprofit.checkplus.h2}</h2>
              <p className="PlugLanding-body">{nonprofit.checkplus.lede}</p>
            </div>
            <a href="#" className="PlugLanding-linkRight">
              {nonprofit.checkplus.link}
            </a>
          </div>

          <div className="PlugLanding-cpGrid">
            {nonprofit.checkplus.features.map((feature, index) => (
              <div className="PlugLanding-cpCard" key={feature.title}>
                <div className="PlugLanding-cpIcon">
                  <span
                    className="PlugLanding-cpDot"
                    style={{ borderRadius: CP_FEATURE_DOTS[index] }}
                  />
                </div>
                <h3 className="PlugLanding-cpTitle">{feature.title}</h3>
                <p className="PlugLanding-cpTxt">{feature.txt}</p>
              </div>
            ))}
          </div>

          <div className="PlugLanding-cpStrip">
            <div className="PlugLanding-cpStripLogos">
              <img src={plugLogo} alt={copy.nav.logoAlt} />
              <span className="PlugLanding-cpStripPlus">+</span>
              <span className="PlugLanding-cpStripName">{nonprofit.checkplus.stripName}</span>
            </div>
            <div className="PlugLanding-cpStripTxt">{nonprofit.checkplus.stripTxt}</div>
            <a href="#" className="PlugLanding-cpStripCta">
              {nonprofit.checkplus.stripCta}
            </a>
          </div>
        </div>

        <div className="PlugLanding-band" id={sectionId("join")}>
          <div className="PlugLanding-intro PlugLanding-introNarrow">
            <div className="PlugLanding-eyebrow">{nonprofit.join.kicker}</div>
            <h2 className="PlugLanding-h2">{nonprofit.join.h2}</h2>
            <p className="PlugLanding-body">{nonprofit.join.lede}</p>
          </div>
          <div className="PlugLanding-grid3">
            {nonprofit.join.steps.map((step) => (
              <div key={step.title}>
                <div className="PlugLanding-stepHead">
                  <span className="PlugLanding-stepNum">{step.n}</span>
                  <span className="PlugLanding-stepRule" />
                </div>
                <h3 className="PlugLanding-h3">{step.title}</h3>
                <p className="PlugLanding-stepTxt PlugLanding-stepTxtLast">{step.txt}</p>
              </div>
            ))}
          </div>
          <div className="PlugLanding-eligibility">
            <div className="PlugLanding-eligibilityTxt">{nonprofit.join.eligibility}</div>
            <a href="#" className="PlugLanding-eligibilityCta">
              {nonprofit.join.eligibilityCta}
            </a>
          </div>
        </div>

        <div className="PlugLanding-section">
          <div className="PlugLanding-quote" style={{ padding: "36px 40px" }}>
            <div>
              <div className="PlugLanding-quoteTxt">{nonprofit.quote.txt}</div>
              <div className="PlugLanding-quoteWho">
                <div className="PlugLanding-quoteMono">{nonprofit.quote.mono}</div>
                <div>
                  <div className="PlugLanding-quoteName">{nonprofit.quote.name}</div>
                  <div className="PlugLanding-quoteMeta">{nonprofit.quote.meta}</div>
                </div>
              </div>
            </div>
            <div className="PlugLanding-photo">{nonprofit.quote.photo}</div>
          </div>
        </div>
      </div>
    );
  }

  renderFaq() {
    const { copy } = this.props;
    const faqs = this.currentFaqs();

    return (
      <div className="PlugLanding-faq" id={sectionId("faq")}>
        <div className="PlugLanding-faqGrid">
          <div>
            <h2 className="PlugLanding-faqTitle">{copy.faq.title}</h2>
            {!!this.props.showFaqContact && (
              <React.Fragment>
                <p className="PlugLanding-faqLede">{copy.faq.lede}</p>
                <a href="#" className="PlugLanding-linkRight" style={{ marginLeft: 0 }}>
                  {copy.faq.contact}
                </a>
              </React.Fragment>
            )}
          </div>
          <div className="PlugLanding-faqList">
            {faqs.map((item, index) => (
              <button
                type="button"
                className="PlugLanding-faqItem"
                key={item.q}
                onClick={this.toggleFaq(index)}
              >
                <div className="PlugLanding-faqQRow">
                  <span className="PlugLanding-faqQ">{item.q}</span>
                  <span className="PlugLanding-faqSign">{this.state.open === index ? "−" : "+"}</span>
                </div>
                <div className={"PlugLanding-faqBody" + (this.state.open === index ? " is-open" : "")}>
                  <p className="PlugLanding-faqA">{item.a}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderCta() {
    const page = this.state.page;
    const cta = this.props.copy.cta[page];

    /* Both buttons are optional, and plug-impact drops them altogether. With
       neither, the panel is a closing statement and the button column would
       otherwise sit there empty. */
    const hasPrimary = !!this.props.showSignupCta && !!cta.primary;
    /* The secondary is "Browse nonprofits" on two pages and a section jump on
       the other two; only the first kind answers to showBrowse. */
    const hasSecondary =
      !!cta.secondary && (!CTA_SECONDARY_BROWSES[page] || !!this.props.showBrowse);
    const hasButtons = !!this.props.showAccountCtas && (hasPrimary || hasSecondary);

    return (
      <div className="PlugLanding-ctaWrap">
        <div className={"PlugLanding-cta" + (hasButtons ? "" : " PlugLanding-ctaSolo")}>
          <div className="PlugLanding-ctaCopy">
            <h2 className="PlugLanding-ctaHeadline">{cta.headline}</h2>
            <p className="PlugLanding-ctaSub">{cta.sub}</p>
          </div>
          {!!hasButtons && (
            <div className="PlugLanding-ctaBtns">
              {!!hasPrimary &&
                this.signupCtaLink({
                  className: "PlugLanding-ctaPrimary",
                  children: cta.primary,
                })}
              {!!hasSecondary &&
                (CTA_SECONDARY_BROWSES[page] ? (
                  this.browseLink({
                    className: "PlugLanding-ctaSecondary",
                    children: cta.secondary,
                  })
                ) : (
                  <a href="#" className="PlugLanding-ctaSecondary">
                    {cta.secondary}
                  </a>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * The sections of the page on screen that the footer can jump to. Filters out
   * the two whose section is switched off, so the footer never advertises an
   * anchor that is not in the document.
   */
  currentSections() {
    const { showCalculator, showFaq } = this.props;

    return (PAGE_SECTIONS[this.state.page] || []).filter((key) => {
      if (key === "calculator") return !!showCalculator;
      if (key === "faq") return !!showFaq;
      return true;
    });
  }

  renderFooter() {
    const { copy, showAudiencePages, showFooterColumns, languageLinks } = this.props;
    const footer = copy.footer;

    /* Only pages that are actually published. With the audience pages hidden
       this leaves What is Plug, which is the page you are already on, so the
       column becomes on-this-page links instead. */
    const pageLinks = showAudiencePages
      ? [
        [PAGES.ABOUT, footer.whoAbout],
        [PAGES.DONORS, footer.whoDonors],
        [PAGES.BUSINESS, footer.whoBusiness],
        [PAGES.NONPROFIT, footer.whoNonprofit],
      ]
      : [];

    return (
      <div className="PlugLanding-footer">
        <div
          className={
            "PlugLanding-footerGrid" + (showFooterColumns ? "" : " PlugLanding-footerGridSimple")
          }
        >
          <div>
            <div className="PlugLanding-footerLogo">
              <img src={plugLogo} alt={copy.nav.logoAlt} />
            </div>
            <p className="PlugLanding-footerBlurb">{footer.blurb}</p>
          </div>

          <div className="PlugLanding-footerCol">
            <div className="PlugLanding-footerHead">{footer.onThisPageHead}</div>
            {pageLinks.map(([page, label]) =>
              this.pageLink(page, { key: page, className: "PlugLanding-footerLink", children: label }),
            )}
            {this.currentSections().map((key) => (
              <a key={key} href={"#" + sectionId(key)} className="PlugLanding-footerLink">
                {copy.sectionLabels[key]}
              </a>
            ))}
          </div>

          {/* Trust and Company are every-page links that do not exist yet — the
              hrefs are all "#". They stay out of the simple footer rather than
              shipping a column of links that go nowhere. */}
          {!!showFooterColumns && (
            <div className="PlugLanding-footerCol">
              <div className="PlugLanding-footerHead">{footer.trustHead}</div>
              <a href="#" className="PlugLanding-footerLink">
                {footer.trustSecurity}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.trustStripe}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.trustSection88}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.trustPrivacy}
              </a>
            </div>
          )}

          {!!showFooterColumns && (
            <div className="PlugLanding-footerCol">
              <div className="PlugLanding-footerHead">{footer.companyHead}</div>
              <a href="#" className="PlugLanding-footerLink">
                {footer.companyAbout}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.companyContact}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.companySupport}
              </a>
              <a href="#" className="PlugLanding-footerLink">
                {footer.companyTerms}
              </a>
            </div>
          )}
        </div>

        {/* Without a header there is no language switcher, and the other two
            translations would be unreachable. */}
        {!!languageLinks && (
          <div className="PlugLanding-footerLangs">
            {languageLinks.map((entry) =>
              entry.code === this.props.lang ? (
                <span key={entry.code} className="PlugLanding-footerLangCurrent">
                  {LANGUAGE_LABELS[entry.code]}
                </span>
              ) : (
                <a key={entry.code} href={entry.href} className="PlugLanding-footerLink">
                  {LANGUAGE_LABELS[entry.code]}
                </a>
              ),
            )}
          </div>
        )}

        <div className="PlugLanding-footerBar">
          <span className="PlugLanding-footerNote">{footer.note}</span>
          <span className="PlugLanding-footerRegion">{footer.region}</span>
        </div>
      </div>
    );
  }

  renderPage() {
    switch (this.state.page) {
      case PAGES.DONORS:
        return this.renderDonors();
      case PAGES.BUSINESS:
        return this.renderBusiness();
      case PAGES.NONPROFIT:
        return this.renderNonprofit();
      default:
        return this.renderAbout();
    }
  }

  render() {
    const { copy, showFaq, i18n, lang } = this.props;

    return (
      <div className={"PlugLanding" + (lang === "en" ? "" : " PlugLanding--cjk")}>
        <Helmet>
          <title>{copy.documentTitle}</title>
        </Helmet>

        {!!this.props.showNav && this.renderNav(i18n)}
        {this.renderPage()}
        {!!showFaq && this.renderFaq()}
        {this.renderCta()}
        {this.renderFooter()}
      </div>
    );
  }
}

PlugLanding.propTypes = {
  copy: PropTypes.object.isRequired,
  lang: PropTypes.oneOf(["en", "zh", "cn"]).isRequired,
  i18n: PropTypes.object.isRequired,
  history: PropTypes.object,
  showPressStrip: PropTypes.bool,
  showCalculator: PropTypes.bool,
  showFaq: PropTypes.bool,
  /* Which of the four pages to open on. The static export renders one page per
     file, so it sets this; in the app it stays on the default. */
  initialPage: PropTypes.oneOf(Object.values(PAGES)),
  /* Given a page, the URL that page lives at. Set only by the static export —
     when absent, page navigation stays as in-component state. */
  hrefForPage: PropTypes.func,
  /* Where the three webapp hand-offs go — "Log in", the sign-up buttons
     ("Create a free account", "Sign up", "Apply to list", …) and "Browse
     nonprofits". They default to the app's own /webapp; the static export
     points them at the app's origin. */
  loginHref: PropTypes.string,
  signupHref: PropTypes.string,
  browseHref: PropTypes.string,
  /* The whole header. plug-impact publishes a single page and turns it off. */
  showNav: PropTypes.bool,
  /* Whether anything links to the three audience pages — nav tabs, the card
     links on What is Plug, the footer, the donor page's cross-links. Off
     everywhere for now: the pages are still built and still routed, so a
     direct URL serves them, but nothing points at them. */
  showAudiencePages: PropTypes.bool,
  /* Every hand-off to the webapp — "Log in", every sign-up button and every
     browse button, on all four pages, in the nav, the heroes and the closing
     CTA. Off means nothing anywhere links to the app. plug-impact uses it to
     publish the site before the app is ready to receive anyone. */
  showAccountCtas: PropTypes.bool,
  /* The nav's "Log in" alone. Narrower than showAccountCtas on purpose: that
     one is all-or-nothing by design, and this hides the one link without
     taking the sign-up and browse buttons with it. Off for now — signing up is
     the only way in we want to advertise. Still gated by showAccountCtas, so
     turning that off hides this whether or not it is set. */
  showLogin: PropTypes.bool,
  /* The nav's own sign-up pill — "Sign up", or the audience pages' wordings.
     The last hand-off to be switched off, so nothing in the header or the body
     now points at the webapp. Still gated by showAccountCtas. */
  showNavCta: PropTypes.bool,
  /* The hero and closing-CTA sign-up button alone — "Create a free account" on
     What is Plug. Same reasoning as showLogin: narrower than showAccountCtas,
     so the nav's "Sign up" and "Browse nonprofits" stay. Off for now. Still
     gated by showAccountCtas. */
  showSignupCta: PropTypes.bool,
  /* The FAQ's "Anything else, …" line and its "Contact support" link. Off
     while that link still points at "#", the same reason showFooterColumns
     exists. The question list itself is unaffected. */
  showFaqContact: PropTypes.bool,
  /* "Browse nonprofits" — the donor hero, the marketplace section and the
     closing CTA's secondary on the two pages that browse. Off for now. Still
     gated by showAccountCtas. */
  showBrowse: PropTypes.bool,
  /* The footer's Trust and Company columns, whose links do not exist yet. Off
     for now, leaving the logo, the tagline and the on-this-page list. */
  showFooterColumns: PropTypes.bool,
  /* [{ code, href }] for the other translations of this page. Set by the
     static export, which has no header to put a switcher in. */
  languageLinks: PropTypes.array,
};

/**
 * LAUNCH STATE (CP-318).
 *
 * The four hand-offs to the webapp are on: the site now offers "Log in", the
 * nav's sign-up pill, "Create a free account" and "Browse nonprofits", all of
 * which lead to real /webapp URLs.
 *
 * Three are still held back, and each for the same reason — turning it on
 * publishes something that is not true or does not work:
 *
 *   showAudiencePages — For donors / businesses / nonprofits still carry the
 *     section 7b inaccuracies described at the top of copyEn.js: the "Plug
 *     takes no cut" claims, the old business rule types, and figures for a
 *     product that has not launched. Several of their own links are "#" too.
 *     Correct that copy in all three languages first; then add them to
 *     EXPORTED_PAGES in plug-impact/build.js, which does not export them
 *     either.
 *
 *   showFooterColumns — every href in the Trust and Company columns is "#",
 *     Privacy and Terms among them.
 *
 *   showFaqContact — "Contact support →" is "#".
 *
 * showAccountCtas remains the master over the four that are on: false hides
 * all of them regardless.
 *
 * The behaviour tests in src/tests/unit/components/PlugLanding.test.js pin
 * this state, so they fail when it changes. That is intended — the failure is
 * the prompt to describe the new state rather than to silence it.
 */
const LAUNCH = {
  showLogin: true,
  showNavCta: true,
  showSignupCta: true,
  showBrowse: true,

  /* Held: see above. Each needs content work, not a flag. */
  showAudiencePages: false,
  showFooterColumns: false,
  showFaqContact: false,
};

PlugLanding.defaultProps = {
  showPressStrip: true,
  showCalculator: true,
  showFaq: true,
  initialPage: PAGES.ABOUT,
  hrefForPage: null,
  loginHref: WEBAPP_LOGIN,
  signupHref: WEBAPP_SIGNUP,
  browseHref: WEBAPP_PATH,
  showNav: true,
  showAccountCtas: true,
  languageLinks: null,
  ...LAUNCH,
};

export default PlugLanding;
